import { HTMLAttributes, MouseEventHandler, ReactNode, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import moment from "moment";
import cn from "classnames";
import InputMask from 'react-input-mask';
import ClearBtn from "src/Components/ClearBtn/ClearBtn";
import { CalendarWidget } from "src/UI";
import { useOutsideClick } from "src/Hooks";
import DateFormater from "src/Utils/DateFormater";
import { getFullOffsetBy, getScrollContainer } from "src/Utils";
import Regex from "src/Utils/Regex";
import { ReactComponent as CalIcon } from "src/Themes/assets/calendar.svg";
import st from "./DateBox.module.scss";

interface IDateBoxProps extends HTMLAttributes<HTMLDivElement> {
	/** Наименование поля */
	name: string;
	/** Значение */
	value: string | Date;
	/** Функция установки значения */
	setValue?: ( date: Date | null ) => void;

	/** Вариант отображения */
	variant?: "gray" | "blue" | "blueControl";

	/** Разрешить очистку значения */
	allowClear?: boolean;
	/** Только для чтения */
	disabled?: boolean;
	/** С временем */
	withTime?: boolean;
	/** Показывать контрол только для чтения */
	showDisabling?: boolean;

	/** Поле с ошибкой */
	isError?: boolean;
	/** Подсказка */
	hint?: string;

	renderIcon?: boolean;
	renderInput?: boolean;

	minDate?: Date;
	maxDate?: Date;
	isArrow?: boolean;
	renderPlaceHolder?: ( date: Date | null, isOpen: boolean ) => ReactNode;
};

const DateBox = ( props: IDateBoxProps ) => {

	const { name, value, setValue, disabled = false, minDate, maxDate, isArrow = false, showDisabling = true,
		renderPlaceHolder, allowClear = false, hint = "", isError = false, variant = "gray",
		renderIcon = true, renderInput = true, className, ...otherProps 
	} = props;

	const wrapperRef = useRef<HTMLDivElement>(null);
	const [ isOpened, setIsOpened ] = useState<boolean>(false);
	const [ position, setPosition ] = useState<PositionType>(null);
	const [ tmpValue, setTmpValue ] = useState<string>('');
	const modalRef = useOutsideClick<HTMLDivElement>( (e) => !!onOutSideClick && onOutSideClick(e), "mouseup" );

	useLayoutEffect( () => {

		const wr = wrapperRef.current;

		const calcOffsetModalPosition = () => {
			if ( !!wr ) {
				const offset = getFullOffsetBy( wr, "rdc");
				let top = wr.offsetTop - offset.scrollTop + wr.offsetHeight + 5;
				let left = wr.offsetLeft - offset.scroll;
				if ( offset.aLeft === 0 ) {
					top = ( offset.top - offset.scrollTop ) + wr.offsetHeight + 5;
					left = offset.left - offset.scroll;
				}
				if ( ( top + 340 ) > window.innerHeight ) {
					top -= ( 334 + wr.clientHeight + 35 );
				}
				if ( (left + wr.clientWidth ) > window.innerWidth ) {
					left -= 380;
				}
				setPosition( { top, left } );
			}
		};

		const onClose = () => setIsOpened(false);

		if ( isOpened ) {
			const scrollContainer = getScrollContainer( wr );
			if ( !!scrollContainer ) {
				scrollContainer.addEventListener( "scroll", onClose );
			}
		}

		calcOffsetModalPosition();

		return () => {
			if ( !wr ) return;
			const scrollContainer = getScrollContainer( wr );
			if ( !!scrollContainer ) {
				scrollContainer.removeEventListener( "scroll", onClose)
			}
		};
	// eslint-disable-next-line
	}, [ isOpened ] );

	useEffect( () => {
		if ( !value ) {
			setTmpValue( '' );
		}
		else if ( typeof value === "string" ) {
			const _val = getDateValue(value);
			setTmpValue( getStringValue(_val) ?? '' );
		}
		else if ( !!value.getDate ) {
			setTmpValue( getStringValue(value) ?? '' );
		}
	}, [ value ] );

	const onChangeHandler = ( newValue: Date | null ) => {
		if ( !!setValue )
			setValue(newValue);
		onSetIsOpen(false);
	};

	const onChangeInputHandler = ( e: any ) => {
		e.persist();
		const _value: string = e.target.value;
		setTmpValue( _value );

		if ( Regex.DateS.test( _value ) ) {
			const newValue = moment( _value, "DD.MM.YYYY" );
			if ( !!setValue )
				setValue( newValue.toDate() );
		} 
		else if ( _value.replaceAll( '.', '').replaceAll( '_', '').length === 0 ) {
			if ( !!setValue )
				setValue(null);
		}
	};

	const onClearHandler: MouseEventHandler<HTMLButtonElement> = ( e ) => {
		e.preventDefault();
		e.stopPropagation();
		onChangeHandler( null );
	};

	const onSetIsOpen = ( force: boolean = false ) => {
		if ( disabled ) return;
		setIsOpened( force );
	};

	const onOutSideClick = ( event: MouseEvent ) => {
		const isFinded = wrapperRef.current && (wrapperRef.current as any).contains( event.target as Node );
		if ( !isFinded ) {
			onSetIsOpen(false);
		}
	};

	const renderBody = () => {
		const val = getDateValue(value);
		const values =  !!val ? [ val ] : []
		return (
			<div ref={modalRef} className={st.modalWrapper}>
				<CalendarWidget
					readOnly={disabled}
					values={values}
					onDateClick={onChangeHandler}
					style={{ position: "absolute", ...position }}
					minDate={minDate}
					maxDate={maxDate}
				/>
			</div>
		);
	};

	const wrapperClasses = cn(st.rdcDatebox, className, st[variant], {
		[st.notHover]: disabled,
		[st.disabled]: ( showDisabling && disabled ),
		[st.active]: isOpened,
		[st.withArrow]: isArrow,
		[st.invalidate]: isError
	});

	const val = getDateValue(value);
	return (
		<>
			<div 
				ref={wrapperRef} 
				className={wrapperClasses} 
				onClick={ () => onSetIsOpen(true) } 
				data-tooltip-id={ isError ? "rdc_tooltip-error" : "rdc_tooltip"}
				data-tooltip-content={hint}
				{...otherProps} 
			>
				{ renderIcon && <CalIcon  /> }
				{
					( renderInput && !renderPlaceHolder ) &&
					<InputMask
						name={name}
						readOnly={disabled}
						alwaysShowMask={false}
						placeholder="ДД.ММ.ГГГГ"
						value={tmpValue} 
						onChange={onChangeInputHandler}
						mask="99.99.9999" 
					/>
				}
				{ !!renderPlaceHolder && renderPlaceHolder(val, isOpened) }
				{ allowClear && <ClearBtn onClick={onClearHandler} /> }
				{
					isArrow && 
					<svg width="33" height="32" viewBox="0 0 33 32" fill="inherit" xmlns="http://www.w3.org/2000/svg">
						<path d="M11.5 13.5L16.5 18.5L21.5 13.5H11.5Z" fill="inherit"/>
					</svg>
				}
			</div>
			{ isOpened && createPortal( renderBody(), document.body) }
		</>
	);
};

/** Тип позиции ( top, left ) */
export type PositionType = {
	top: number;
	left: number;
} | null;

/** Возвращает строкое представление значения */
const getStringValue = ( value: Date | null ): string | undefined => !!value ? DateFormater.toShortString(value) : '';

/** Возвращает дату */
const getDateValue = ( value: Date | string ): Date | null => {
	if ( !value || value === "Invalid date" ) return null;
	if ( typeof value === "object" && !!value?.getTime ) {
		return value;
	}
	return moment( value, "DD.MM.YYYY" ).toDate();
};

export default DateBox;
